import { computed, defineComponent } from "vue";
import { mapState, mapActions } from "pinia";
import { Container } from "aurelia-dependency-injection";
import { useVuelidate } from "@vuelidate/core";
import { helpers, minValue, required, requiredIf } from "@vuelidate/validators";

import { useHospiceConvertToPending } from "./store/useHospiceConvertToPending";
import { PatientService } from "../../../services/patient.service";
import { ToastrService } from "../../../services/toastr.service";
import { ReferralService } from "../../../services/referral-service";
import { HospiceConvertToPendingPatient } from "../../../resources-vue/vue-models/hospice-convertToPending-details";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import { formatName } from "../../../common/format-name";
import { HospiceHispanicEthnicitiesEnum } from "../../../enums/enums";

import PageLayout from "../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import Demographics from "./components/Demographics/Demographics.vue";
import AdvanceDirectives from "./components/AdvanceDirectives/AdvanceDirectives.vue";
import PharmacyAndDME from "./components/PharmacyAndDME/PharmacyAndDME.vue";
import MciLayout from "../../../resources-vue/vue-elements/MciLayout/Mcilayout.vue";
import Physicians from "./components/Physicians/Physicians.vue";
import PayerInformation from "./components/PayerInformation/PayerInformation.vue";
import EmergencyPreparedness from "./components/EmergencyPreparedness/EmergencyPreparedness.vue";
import ReferralInformation from "./components/ReferralInformation/ReferralInformation.vue";
import AuthorizedContact from "./components/AuthorizedContact/AuthorizedContact.vue";
import Prompt from "../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import ClinicalInformation from "./components/ClinicalInformation/CinicalInformation.vue";
import TabsLayout from "../../../resources-vue/vue-custom-elements/TabsLayout/TabsLayout.vue";
import moment from "moment";

export default defineComponent({
    components: {
        PageLayout,
        Demographics,
        AdvanceDirectives,
        Physicians,
        PharmacyAndDME,
        MciLayout,
        PayerInformation,
        EmergencyPreparedness,
        ReferralInformation,
        AuthorizedContact,
        ClinicalInformation,
        TabsLayout
    },
    data(vm) {
        return {
            tabs: [
                { name: "Demographics", id: 1 },
                { name: "Payer Information", id: 2 },
                { name: "Clinical Information", id: 3 },
                { name: "Physicians", id: 4 },
                { name: "Pharmacy and DME", id: 5 },
                { name: "Authorized Contacts", id: 6 },
                { name: "Emergency Preparedness", id: 7 },
                { name: "Advance Directives", id: 8 },
                { name: "Referral Information", id: 9 }
            ],
            activeTab: 1,
            isFormLoading: false,
            isLoadError: false,
            isSaveInProgress: false,
            _patientService: null,
            _referralService: null,
            _toastrService: null,
            dialogRef: null,
            contentWrapperHos: "",
            formatName,
            v$: useVuelidate()
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef),
            v: computed(() => this.v$)
        };
    },

    computed: {
        ...mapState(useHospiceConvertToPending, [
            "patientId",
            "accountId",
            "patientDetails",
            "application",
            "patientReferralId"
        ])
    },

    created() {
        this._patientService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this.contentWrapperHos = "content-wrapper-hos";
        if (this.$route.query.patientId) {
            this.setPatientId(this.$route.query.patientId);
        }
        if (this.$route.query.accountId) {
            this.setAccountId(this.$route.query.accountId);
        }
        if (this.$route.query.application) {
            this.setApplication(this.$route.query.application);
        }
        if (this.$route.query.patientReferralId) {
            this.setPatientReferralId(this.$route.query.patientReferralId);
        }
        this.getPatientDetails();
        this.contentWrapperHos = "content-wrapper-hos";
    },

    validations() {
        return {
            patientDetails: {
                medicalRecordNumber: { required },
                firstName: { required },
                lastName: { required },
                dateOfBirth: { required },
                gender: { required },
                socialSecurityNumber: { required },
                locationId: { required },
                phoneNumbers: [
                    {
                        number: { required },
                        type: { minValue: minValue(1) }
                    }
                ],
                primaryLanguage: { required },
                medicareBeneficiaryIdentification: {
                    required: requiredIf(() => {
                        return !this.patientDetails.medicareBeneficiaryIdentificationNA;
                    })
                },
                admissionType: { required },
                admissionSource: { required },
                benefitPeriodNumber: { required },
                certifyingPhysicianId: { required },
                attendingPhysicianId: { required },
                careTeamId: { required },
                clinicalManagerId: { required },
                referralDate: { 
                    required: helpers.withMessage("Referral Date is required", required),
                    referralDateNotBeforeInquiryDate: helpers.withMessage(
                        "The referral date must be on or after the inquiry date.",
                        (value:string | null) => {
                            if(!value || !this.patientDetails.inquiryDate) return true;
                            const referral = moment(value, "MM/DD/YYYY", true);
                            const inquiry = moment(this.patientDetails.inquiryDate, "MM/DD/YYYY", true);
                            if(!referral.isValid() || !inquiry.isValid()) return true;
                            return referral.isSameOrAfter(inquiry, "day");
                        }
                    ),
                 },
                hispanicEthnicityValues: {
                    required
                },
                race: { required }
            }
        };
    },

    methods: {
        ...mapActions(useHospiceConvertToPending, {
            setPatientId: "SET_PATIENT_ID",
            setAccountId: "SET_ACCOUNT_ID",
            setPatientDetails: "SET_PATIENT_DETAILS",
            setApplication: "SET_APPLICATION",
            setPatientReferralId: "SET_PATIENT_REFERRAL_ID"
        }),
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),

        handleTabChange(tab: any) {
            this.activeTab = tab.id;
        },
        async getPatientDetails() {
            const payload = {
                patientId: this.patientId,
                agencyId: this.accountId,
                application: this.$route.query.application
            };
            try {
                this.showLoading(true);
                this.isLoadError = false;
                this.isFormLoading = true;
                const response = await this._patientService.getHospicePatientById(payload);

                const slimPayload = {
                    patientId: this.patientId,
                    accountId: this.accountId,
                    application: this.$route.query.application
                };
                const slimResponse = await this._patientService.getAdmissionInfo(this.patientId as string, slimPayload);
                const patientData = new HospiceConvertToPendingPatient(response.data.model);
                patientData.benefitPeriodNumber = slimResponse.BenefitPeriodNumber;
                this.setPatientDetails(patientData);

                this.activeTab = 1;
            } catch (err) {
                this.showLoading(false);
                console.error(err);
                this.isLoadError = true;
                this._toastrService.error({
                    title: `Error - There was problem loading patient details`,
                    message: `Please try again`
                });
            } finally {
                this.showLoading(false);
                this.isFormLoading = false;
            }
        },
        handleCancelConvertToPending() {
            let promptOptions = {
                message:
                    "Do you want to leave the page? This patient will remain on the Referral List and your updates will not be saved.",
                okText: "Leave Page",
                cancelText: "Stay Here"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data === "success") {
                        this.$router.back();
                    }
                }
            });
        },
        handlePrevious() {
            this.activeTab = this.activeTab - 1;
            this.v$.$reset();
        },
        handleNext() {
            this.activeTab = this.activeTab + 1;
            this.v$.$reset();
        },
        async handleSave() {
            let data = await this._patientService.getServiceLocations(this.patientId, {
                application: Number(this.application),
                accountId: this.accountId,
                page: 1,
                pageSize: 10
            });
            let isError = false;
            if (
                this.patientDetails.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                ) &&
                this.patientDetails.hispanicEthnicityValues?.length > 1
            ) {
                isError = true;
            } else if (
                this.patientDetails.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientUnableToRespond
                ) &&
                this.patientDetails.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                )
            ) {
                isError = true;
            } else if (
                this.patientDetails.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.NoNotOfHispanicLatinoOrSpanishOrigin
                ) &&
                (this.patientDetails.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.YesMexicanAmericanChicano
                ) ||
                    this.patientDetails.hispanicEthnicityValues?.includes(
                        HospiceHispanicEthnicitiesEnum.YesAnotherHispanicLatinoOrSpanishOrigin
                    ) ||
                    this.patientDetails.hispanicEthnicityValues?.includes(
                        HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                    ))
            ) {
                isError = true;
            }
            if (data.items.length && !isError) {
                await this.v$.$validate();
                if(this.v$.patientDetails.referralDate.$invalid){
                    this._toastrService.error({
                        title: "Error",
                        message: this.v$.patientDetails.referralDate.$errors[0].$message ?? "Please check the referral date."
                    });
                    return

                }
                const payload = {
                    application: Number(this.application),
                    accountId: this.accountId,
                    id: this.patientDetails.id,
                    userId: this.patientDetails.id,
                    ...this.patientDetails
                };
                try {
                    this.isSaveInProgress = true;
                    const response = await this._patientService.savePatientDetails(payload);
                    if (response.isSuccess) {
                        this._toastrService.success({
                            title: "Patient Updated",
                            message: `The patient ${this.patientDetails.firstName} ${this.patientDetails.lastName} has been successfully updated.`
                        });
                        if (this.activeTab === 9) {
                            const requestBody = {
                                id: this.patientDetails.currentReferralId,
                                query: {
                                    application: Number(this.application),
                                    accountId: this.accountId
                                }
                            };
                            const responseData = await this._referralService.hosConvertToPending(requestBody);
                            if (responseData.isSuccess) {
                                this._toastrService.success({
                                    title: "Success",
                                    message: `You have successfully updated the patient`
                                });
                                this.$router.push("/axxess-central/intake/referral-list");
                            } else {
                                this._toastrService.error({
                                    title: "Error",
                                    message: `${responseData.message}`
                                });
                            }
                        } else {
                            this.$router.push("/axxess-central/intake/referral-list");
                        }
                    } else {
                        this._toastrService.error({
                            title: "Failed to save details",
                            message: `${response.message}`
                        });
                    }
                } catch (err) {
                    this._toastrService.error({
                        title: `Error - There was problem saving patient details`,
                        message: `Please try again`
                    });
                } finally {
                    this.isSaveInProgress = false;
                }
            } else {
                if (isError) {
                    this._toastrService.error({
                        title: "Error",
                        message:
                            "Invalid Combination for HOPE Ethnicity: Y must be selected alone; A cannot be combined with B-E or Y; B-E cannot be combined with Y; X cannot be combined with Y."
                    });
                }
                if (!data.items.length) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Service Location is required`
                    });
                }
            }
        }
    }
});
